<template>
  <div>
    <!-- Header -->
    <AppHeader />

    <!-- Course Detail Section -->
    <div v-if="!loading && !error && course" class="course-detail">
      <!-- Hero Section with Course Image -->
      <section class="course-hero">
        <div class="container">
          <div class="course-hero-content">
            <div class="course-hero-info">
              <h1 class="fade-in-up">{{ course.name }}</h1>
              <div class="course-meta fade-in-up">
                <div class="course-price-badge">
                  <template v-if="course.is_free">
                    <span class="badge-free">Бесплатный курс</span>
                  </template>
                  <template v-else>
                    <span class="badge-paid">{{ formatPrice(course.price) }} ₽</span>
                  </template>
                </div>
              </div>
            </div>
            <div class="course-hero-image" v-if="course.photo">
              <img :src="course.photo" :alt="course.name" />
            </div>
          </div>
        </div>
      </section>

      <!-- Course Description Section -->
      <UISection>
        <template #default>
          <div class="course-description-wrapper">
            <div class="course-main">
              <div class="course-description" v-html="course.description.html"></div>
              
              <!-- Course Program -->
              <div v-if="modules.length > 0" class="course-program">
                <h2 class="program-title">Программа курса</h2>
                <div class="modules-list">
                  <div v-for="(module, index) in modules" :key="module.id" class="module-item">
                    <div class="module-header">
                      <span class="module-number">{{ index + 1 }}</span>
                      <div class="module-info">
                        <h3>{{ module.title }}</h3>
                        <p v-if="module.description">{{ module.description }}</p>
                        <span class="lessons-count">{{ module.lessons.length }} {{ module.lessons.length === 1 ? 'урок' : 'уроков' }}</span>
                      </div>
                    </div>
                    <ul class="lessons-list" v-if="module.lessons.length > 0">
                      <li v-for="lesson in module.lessons" :key="lesson.id" class="lesson-item">
                        <span class="lesson-icon">{{ getLessonIcon(lesson.lesson_type) }}</span>
                        <div class="lesson-details">
                          <span class="lesson-title">{{ lesson.title }}</span>
                          <span v-if="lesson.duration_minutes" class="lesson-duration">{{ lesson.duration_minutes }} мин</span>
                        </div>
                      </li>
                    </ul>
                  </div>
                </div>
              </div>
            </div>
            
            <!-- Action Section -->
            <div class="course-action">
              <div v-if="paymentStatus" class="payment-status-banner">
                {{ paymentStatus }}
              </div>
              <!-- Форма покупки для продуктов без курса -->
              <div v-if="!course.course_id && !course.is_free" class="action-card purchase-form-card">
                <p class="action-price">{{ formatPrice(course.price) }} ₽</p>
                <p class="action-description">
                  Заполните форму для покупки
                </p>
                <form @submit.prevent="handlePublicPurchase" class="purchase-form">
                  <div class="form-group">
                    <label>Имя *</label>
                    <input type="text" v-model="purchaseForm.name" required placeholder="Ваше имя" />
                  </div>
                  <div class="form-group">
                    <label>Email *</label>
                    <input type="email" v-model="purchaseForm.email" required placeholder="your@email.com" />
                  </div>
                  <button type="submit" class="action-button" :disabled="purchasing">
                    {{ purchasing ? 'Обработка...' : 'Купить' }}
                  </button>
                  <p v-if="purchaseError" class="error-message">{{ purchaseError }}</p>
                </form>
                <transition name="fade">
                  <div
                    v-if="showPaymentModal"
                    class="payment-modal-overlay"
                    @click.self="closePaymentModal"
                  >
                    <div class="payment-modal">
                      <h3>Оплата заказа</h3>
                      <div id="yookassa-widget-container"></div>
                    </div>
                  </div>
                </transition>
              </div>
              <!-- Обычная карточка для продуктов с курсом - всегда перенаправляем в портал -->
              <div v-else class="action-card" :class="{ enrolled: hasAccess && progressPercent < 100, completed: progressPercent === 100 }">
                <h3>{{ getActionTitle() }}</h3>
                <p v-if="!hasAccess && !course.is_free" class="action-price">{{ formatPrice(course.price) }} ₽</p>
                <div v-if="hasAccess && progressPercent > 0" class="action-progress">
                  <div class="progress-bar">
                    <div class="progress-fill" :style="{ width: progressPercent + '%' }" :class="{ completed: progressPercent === 100 }"></div>
                  </div>
                  <span class="progress-text">{{ progressPercent }}%</span>
                </div>
                <p class="action-description">
                  {{ getActionDescription() }}
                </p>
                <UIButton 
                  variant="primary" 
                  class="action-button"
                  @click="handleAction"
                >
                  {{ getActionButtonText() }}
                </UIButton>
                <p v-if="!isLoggedIn" class="action-note">
                  Войдите в личный кабинет для покупки и доступа к курсу
                </p>
              </div>
            </div>
          </div>
        </template>
      </UISection>

      <!-- Contact Section -->
      <section class="course-cta">
        <div class="container">
          <h2>Остались вопросы?</h2>
          <p>Свяжитесь с нами, и мы расскажем о курсе подробнее</p>
          <UIButton to="/contacts#contact-methods" variant="outline" class="cta-button">Связаться</UIButton>
        </div>
      </section>
    </div>

    <!-- Loading state -->
    <div v-if="loading" class="loading-state">
      <div class="loading-spinner"></div>
      <p>Загрузка курса...</p>
    </div>

    <!-- Error state -->
    <div v-if="error" class="error-state">
      <div class="container">
        <i class="fas fa-exclamation-circle"></i>
        <h2>Ошибка загрузки курса</h2>
        <p>{{ error }}</p>
        <UIButton @click="fetchCourse" variant="primary">Попробовать снова</UIButton>
        <UIButton to="/courses" variant="outline" style="margin-left: 12px">Вернуться к курсам</UIButton>
      </div>
    </div>

    <!-- Footer -->
    <AppFooter />
  </div>
</template>

<script>
import AppHeader from '../components/AppHeader.vue';
import AppFooter from '../components/ui/UIFooter.vue';
import UISection from '../components/ui/UISection.vue';
import UIButton from '../components/ui/UIButton.vue';
import axios from 'axios';
import { addSchemaScript, getCourseSchema, getBreadcrumbSchema, clearAllSchemas } from '../utils/schema';

export default {
  name: 'CourseDetail',
  components: {
    AppHeader,
    AppFooter,
    UISection,
    UIButton
  },
  data() {
    return {
      course: null,
      modules: [],
      loading: true,
      error: null,
      hasAccess: false,
      isCompleted: false,
      progressPercent: 0,
      isLoggedIn: false,
      purchasing: false,
      purchaseError: '',
      showPaymentModal: false,
      paymentStatus: '',
      purchaseForm: {
        name: '',
        email: '',
        phone: ''
      }
    };
  },
  mounted() {
    this.fetchCourse();
    this.checkPaymentStatus();
  },
  watch: {
    '$route.params.slug'() {
      this.fetchCourse();
      this.checkPaymentStatus();
    },
    '$route.query.payment'() {
      this.checkPaymentStatus();
    }
  },
  methods: {
    checkPaymentStatus() {
      const status = this.$route.query.payment;
      if (status === 'success') {
        this.paymentStatus = 'Спасибо! Оплата прошла успешно. На ваш email отправлено подтверждение.';
      } else if (status === 'fail') {
        this.paymentStatus = 'Оплата не прошла. Попробуйте ещё раз.';
      } else {
        this.paymentStatus = '';
      }
    },
    async fetchCourse() {
      this.loading = true;
      this.error = null;
      try {
        const slug = this.$route.params.slug;
        if (!slug) {
          throw new Error('Slug не указан');
        }
        
        // Всегда получаем продукт по slug
        let response;
        try {
          response = await axios.get(`/api/v1/public/products/${slug}`);
        } catch (err) {
          // Если не нашли по slug, пробуем как ID (для обратной совместимости со старыми продуктами)
          if (err.response?.status === 404 && /^\d+$/.test(slug)) {
            response = await axios.get(`/api/v1/public/courses/${slug}`);
          } else {
            throw err;
          }
        }
        
        this.course = response.data;
        
        // Обновляем заголовок страницы
        if (this.course && this.course.name) {
          document.title = `${this.course.name} - Мама я в Китае`;
          // Обновляем мета-тег description
          let metaDescription = document.querySelector('meta[name="description"]');
          if (metaDescription && this.course.description && this.course.description.html) {
            // Извлекаем текст из HTML описания
            const tempDiv = document.createElement('div');
            tempDiv.innerHTML = this.course.description.html;
            const textContent = tempDiv.textContent || tempDiv.innerText || '';
            const shortDescription = textContent.substring(0, 160).trim();
            metaDescription.setAttribute('content', shortDescription || `Курс китайского языка: ${this.course.name}`);
          }
          // Обновляем Open Graph теги
          let ogTitle = document.querySelector('meta[property="og:title"]');
          if (ogTitle) {
            ogTitle.setAttribute('content', `${this.course.name} - Мама я в Китае`);
          }
          
          // Добавляем Schema.org разметку для Яндекс
          clearAllSchemas()
          const courseSchema = getCourseSchema(this.course);
          if (courseSchema) {
            addSchemaScript(courseSchema, 'course');
          }
          
          // Добавляем хлебные крошки
          const breadcrumbs = getBreadcrumbSchema([
            { name: 'Главная', url: window.location.origin },
            { name: 'Курсы', url: window.location.origin + '/courses' },
            { name: this.course.name, url: window.location.href }
          ]);
          addSchemaScript(breadcrumbs, 'breadcrumbs');
        }
        
        // Проверяем авторизацию и доступ (используем id из ответа)
        if (this.course && this.course.id) {
          await this.checkAccess(this.course.id);
          
          // Загружаем программу курса (модули) только если есть курс
          if (this.course.course_id) {
            await this.fetchCourseProgram();
          }
        }
      } catch (error) {
        console.error('Ошибка загрузки курса:', error);
        if (error.response?.status === 404) {
          this.error = 'Курс не найден или недоступен';
        } else {
          this.error = 'Не удалось загрузить курс. Пожалуйста, попробуйте позже.';
        }
      } finally {
        this.loading = false;
      }
    },
    async fetchCourseProgram() {
      if (!this.course || !this.course.id) return;
      
      try {
        // Получаем модули курса через публичный API (используем id из курса)
        const response = await axios.get(`/api/v1/public/courses/${this.course.id}/program`);
        this.modules = response.data;
      } catch (error) {
        console.error('Ошибка загрузки программы курса:', error);
        // Не критично, просто не показываем программу
        this.modules = [];
      }
    },
    async checkAccess(productId) {
      const token = localStorage.getItem('studentAccessToken');
      this.isLoggedIn = !!token;
      
      if (!token) {
        this.hasAccess = false;
        this.isCompleted = false;
        this.progressPercent = 0;
        return;
      }

      // Проверяем доступ только для продуктов с курсом
      if (!this.course || !this.course.course_id) {
        this.hasAccess = false;
        this.isCompleted = false;
        this.progressPercent = 0;
        return;
      }

      try {
        const response = await axios.get(`/api/v1/school/products/${productId}/access`, {
          headers: { 'Authorization': `Bearer ${token}` }
        });
        this.hasAccess = response.data.has_access;
        this.isCompleted = response.data.is_completed || false;
        this.progressPercent = response.data.progress_percent || 0;
      } catch (err) {
        console.error('Ошибка проверки доступа:', err);
        this.hasAccess = false;
        this.isCompleted = false;
        this.progressPercent = 0;
      }
    },
    formatPrice(price) {
      if (!price) return '0';
      return new Intl.NumberFormat('ru-RU').format(price);
    },
    handleAction() {
      if (!this.course || !this.course.id) return;
      
      // Продукты с курсом всегда перенаправляются в портал студента
      const slug = this.course.slug || this.course.id;
      
      // Если не авторизован - переходим на страницу входа с редиректом
      if (!this.isLoggedIn) {
        window.location.href = `/school/login?redirect=/school/product/${slug}`;
        return;
      }
      
      // Если уже зачислен - переходим к обучению
      if (this.hasAccess) {
        window.location.href = `/school/product/${slug}/learn`;
        return;
      }
      
      // Если не зачислен - переходим на страницу продукта в личном кабинете
      window.location.href = `/school/product/${slug}`;
    },
    getActionTitle() {
      if (!this.isLoggedIn || !this.hasAccess) {
        return this.course?.is_free ? 'Начните обучение' : 'Купите курс';
      }
      if (this.progressPercent === 100) {
        return 'Курс завершен';
      }
      if (this.progressPercent > 0) {
        return 'Продолжить обучение';
      }
      return 'Начать обучение';
    },
    getActionDescription() {
      if (!this.isLoggedIn || !this.hasAccess) {
        return this.course?.is_free 
          ? 'Этот курс доступен бесплатно для всех желающих' 
          : 'Получите доступ ко всем материалам курса';
      }
      if (this.progressPercent === 100) {
        return 'Вы прошли все уроки! Материалы остаются доступными';
      }
      if (this.progressPercent > 0) {
        return `Пройдено ${this.progressPercent}% курса`;
      }
      return 'Доступ к материалам курса открыт';
    },
    getActionButtonText() {
      if (!this.isLoggedIn) {
        return 'Войти и начать';
      }
      if (!this.hasAccess) {
        return this.course?.is_free ? 'Участвовать' : 'Купить курс';
      }
      if (this.progressPercent === 100) {
        return 'Начать заново';
      }
      if (this.progressPercent > 0) {
        return 'Продолжить →';
      }
      return 'Начать →';
    },
    getLessonIcon(lessonType) {
      const icons = {
        'video': '📹',
        'text': '📄',
        'quiz': '📝',
        'assignment': '✍️'
      };
      return icons[lessonType] || '📚';
    },
    async handlePublicPurchase() {
      if (!this.course || this.course.course_id || this.course.is_free) {
        return;
      }
      
      this.purchasing = true;
      this.purchaseError = '';
      
      try {
        // Создаем платеж
        const response = await axios.post('/api/v1/public/product-purchases', {
          product_id: this.course.id,
          customer_name: this.purchaseForm.name,
          customer_email: this.purchaseForm.email,
          customer_phone: null
        });
        
        if (!response.data?.confirmation_token) {
          throw new Error('Не удалось создать платеж');
        }
        
        // Загружаем скрипт YooKassa если еще не загружен
        if (!window.YooMoneyCheckoutWidget) {
          const script = document.createElement('script');
          script.src = 'https://yookassa.ru/checkout-widget/v1/checkout-widget.js';
          script.onload = () => this.openYooKassaWidget(response.data.confirmation_token);
          document.head.appendChild(script);
        } else {
          this.openYooKassaWidget(response.data.confirmation_token);
        }
      } catch (error) {
        console.error('Ошибка покупки:', error);
        this.purchaseError = error.response?.data?.detail || error.message || 'Не удалось оформить покупку';
        this.purchasing = false;
      }
    },
    openYooKassaWidget(token) {
      if (!window.YooMoneyCheckoutWidget) {
        this.purchaseError = 'Виджет YooKassa не загружен';
        this.purchasing = false;
        return;
      }

      this.showPaymentModal = true;

      // Ждём, пока Vue смонтирует модальное окно и контейнер
      this.$nextTick(() => {
        const container = document.getElementById('yookassa-widget-container');
        if (!container) {
          this.purchaseError = 'Не удалось инициализировать виджет оплаты.';
          this.purchasing = false;
          this.showPaymentModal = false;
          return;
        }

        const widget = new window.YooMoneyCheckoutWidget({
          confirmation_token: token,
          return_url: window.location.href + '?payment=success',
          customization: {
            colors: {
              control_primary: '#e63946',
              control_primary_content: '#ffffff'
            }
          },
          error_callback: (error) => {
            if (error?.type === 'window_closed_by_user') {
              this.purchasing = false;
              this.showPaymentModal = false;
              return;
            }
            this.purchaseError = error?.message || 'Ошибка в виджете оплаты';
            this.purchasing = false;
            this.showPaymentModal = false;
          }
        });
        
        widget.on('success', () => {
          this.showPaymentModal = false;
          this.purchaseForm = { name: '', email: '', phone: '' };
          this.purchasing = false;
          alert('Спасибо! Оплата прошла успешно. На ваш email отправлено подтверждение.');
        });
        
        widget.on('fail', () => {
          this.purchaseError = 'Оплата не прошла. Попробуйте ещё раз.';
          this.purchasing = false;
          this.showPaymentModal = false;
        });
        
        widget.render('yookassa-widget-container');
      });
    },
    closePaymentModal() {
      this.showPaymentModal = false;
    }
  }
};
</script>

<style scoped>
/* Hero Section */
.course-hero {
  background: linear-gradient(135deg, #e63946 0%, #d62839 100%);
  color: white;
  padding: 60px 20px;
}

.course-hero-content {
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: 48px;
  align-items: center;
  max-width: 1200px;
  margin: 0 auto;
}

.course-hero-info h1 {
  font-size: 48px;
  font-weight: 700;
  margin-bottom: 24px;
  line-height: 1.2;
}

.course-meta {
  display: flex;
  gap: 16px;
  align-items: center;
  flex-wrap: wrap;
}

.course-price-badge {
  font-size: 28px;
  font-weight: 700;
}

.badge-free {
  background: #10b981;
  color: white;
  padding: 12px 24px;
  border-radius: 8px;
  display: inline-block;
}

.badge-paid {
  background: white;
  color: #e63946;
  padding: 12px 24px;
  border-radius: 8px;
  display: inline-block;
}

.course-hero-image {
  border-radius: 16px;
  overflow: hidden;
  box-shadow: 0 20px 60px rgba(0, 0, 0, 0.3);
}

.course-hero-image img {
  width: 100%;
  height: 400px;
  object-fit: cover;
}

/* Course Description */
.course-description-wrapper {
  display: grid;
  grid-template-columns: 2fr 1fr;
  gap: 48px;
  max-width: 1200px;
  margin: 0 auto;
}

.course-main {
  display: flex;
  flex-direction: column;
  gap: 48px;
}

.course-description {
  font-size: 18px;
  line-height: 1.8;
  color: #374151;
}

/* Переопределяем стандартные стили Quill для списков */
.course-description :deep(.ql-editor) {
  padding: 0;
}

.course-description :deep(.ql-editor ul),
.course-description :deep(.ql-editor ol) {
  padding-left: 0;
}

/* Агрессивное скрытие стандартной нумерации браузера */
.course-description :deep(ol),
.course-description :deep(ul) {
  list-style: none !important;
  list-style-type: none !important;
  padding-left: 40px;
  margin: 24px 0;
}

.course-description :deep(ol li),
.course-description :deep(ul li) {
  list-style: none !important;
  list-style-type: none !important;
}

.course-description :deep(ol li::marker),
.course-description :deep(ul li::marker) {
  content: '' !important;
  display: none !important;
}

/* Скрываем элементы ql-ui от Quill */
.course-description :deep(.ql-ui) {
  display: none !important;
}

/* Заголовки */
.course-description :deep(h1) {
  font-size: 40px;
  font-weight: 800;
  color: #111827;
  margin-top: 48px;
  margin-bottom: 24px;
  line-height: 1.2;
  border-bottom: 3px solid #e63946;
  padding-bottom: 12px;
}

.course-description :deep(h2) {
  font-size: 32px;
  font-weight: 700;
  color: #111827;
  margin-top: 40px;
  margin-bottom: 20px;
  line-height: 1.3;
  position: relative;
  padding-left: 20px;
}

.course-description :deep(h2)::before {
  content: '';
  position: absolute;
  left: 0;
  top: 50%;
  transform: translateY(-50%);
  width: 6px;
  height: 28px;
  background: linear-gradient(180deg, #e63946 0%, #d62839 100%);
  border-radius: 3px;
}

.course-description :deep(h3) {
  font-size: 26px;
  font-weight: 600;
  color: #1f2937;
  margin-top: 32px;
  margin-bottom: 16px;
  line-height: 1.4;
}

.course-description :deep(h4) {
  font-size: 22px;
  font-weight: 600;
  color: #374151;
  margin-top: 24px;
  margin-bottom: 12px;
}

.course-description :deep(h5) {
  font-size: 18px;
  font-weight: 600;
  color: #4b5563;
  margin-top: 20px;
  margin-bottom: 10px;
}

.course-description :deep(h6) {
  font-size: 16px;
  font-weight: 600;
  color: #6b7280;
  margin-top: 16px;
  margin-bottom: 8px;
  text-transform: uppercase;
  letter-spacing: 0.5px;
}

/* Абзацы */
.course-description :deep(p) {
  margin-bottom: 20px;
  text-align: justify;
}

.course-description :deep(p:first-of-type) {
  font-size: 20px;
  color: #1f2937;
  font-weight: 500;
}

/* Ненумерованные списки */
.course-description :deep(ul:not([data-list])) {
  margin: 24px 0;
  padding-left: 0;
  list-style: none;
}

.course-description :deep(ul:not([data-list]) li) {
  margin-bottom: 8px;
  padding-left: 40px;
  position: relative;
  line-height: 1.5;
}

.course-description :deep(ul:not([data-list]) li)::before {
  content: '';
  position: absolute;
  left: 0;
  top: -2px;
  width: 28px;
  height: 28px;
  background: linear-gradient(135deg, #e63946 0%, #d62839 100%);
  border-radius: 50%;
}

.course-description :deep(ul:not([data-list]) ul) {
  margin: 12px 0;
  padding-left: 20px;
}

.course-description :deep(ul:not([data-list]) ul li)::before {
  width: 24px;
  height: 24px;
  background: #d62839;
  top: -2px;
}

/* Ненумерованные списки Quill (Quill использует <ol> с data-list="bullet" на <li>) */
.course-description :deep(ol li[data-list="bullet"]) {
  margin-bottom: 8px !important;
  padding-left: 40px !important;
  position: relative;
  line-height: 1.5 !important;
  list-style: none !important;
  list-style-type: none !important;
}

.course-description :deep(ol li[data-list="bullet"])::marker {
  content: '' !important;
  display: none !important;
}

.course-description :deep(ol li[data-list="bullet"])::before {
  content: '' !important;
  position: absolute;
  left: 0;
  top: -2px;
  width: 28px;
  height: 28px;
  background: linear-gradient(135deg, #e63946 0%, #d62839 100%);
  border-radius: 50%;
}

/* Нумерованные списки */
.course-description :deep(ol:not([data-list])) {
  margin: 24px 0;
  padding-left: 0;
  counter-reset: item;
  list-style: none;
}

.course-description :deep(ol:not([data-list]) li) {
  margin-bottom: 8px;
  padding-left: 40px;
  position: relative;
  line-height: 1.5;
}

.course-description :deep(ol:not([data-list]) li)::before {
  content: counter(item);
  counter-increment: item;
  position: absolute;
  left: 0;
  top: 0;
  width: 28px;
  height: 28px;
  background: linear-gradient(135deg, #e63946 0%, #d62839 100%);
  color: white;
  border-radius: 50%;
  display: flex;
  align-items: center;
  justify-content: center;
  font-weight: 700;
  font-size: 13px;
}

.course-description :deep(ol:not([data-list]) ol) {
  margin: 12px 0;
  padding-left: 20px;
}

.course-description :deep(ol:not([data-list]) ol li)::before {
  width: 24px;
  height: 24px;
  font-size: 13px;
  background: linear-gradient(135deg, #f87171 0%, #ef4444 100%);
}

/* Нумерованные списки Quill (Quill использует <ol> с data-list="ordered" на <li>) */
.course-description :deep(ol li[data-list="ordered"]) {
  margin-bottom: 8px !important;
  padding-left: 40px !important;
  position: relative;
  line-height: 1.5 !important;
  list-style: none !important;
  list-style-type: none !important;
}

.course-description :deep(ol li[data-list="ordered"])::marker {
  content: '' !important;
  display: none !important;
}

.course-description :deep(ol li[data-list="ordered"]:first-child) {
  counter-reset: item;
}

.course-description :deep(ol li[data-list="ordered"])::before {
  content: counter(item) !important;
  counter-increment: item;
  position: absolute;
  left: 0;
  top: -2px;
  width: 28px;
  height: 28px;
  background: linear-gradient(135deg, #e63946 0%, #d62839 100%);
  color: white;
  border-radius: 50%;
  display: flex;
  align-items: center;
  justify-content: center;
  font-weight: 700;
  font-size: 13px;
}

/* Изображения */
.course-description :deep(img) {
  max-width: 100%;
  height: auto;
  border-radius: 12px;
  margin: 32px 0;
  box-shadow: 0 8px 24px rgba(0, 0, 0, 0.12);
  transition: transform 0.3s ease, box-shadow 0.3s ease;
}

.course-description :deep(img:hover) {
  transform: translateY(-4px);
  box-shadow: 0 12px 32px rgba(0, 0, 0, 0.18);
}

/* Аудио */
.course-description :deep(audio) {
  width: 100%;
  max-width: 100%;
  margin: 28px 0;
  border-radius: 10px;
  box-shadow: 0 4px 12px rgba(0, 0, 0, 0.08);
}

/* Текстовое форматирование */
.course-description :deep(strong),
.course-description :deep(b) {
  color: #111827;
  font-weight: 700;
}

.course-description :deep(em),
.course-description :deep(i) {
  font-style: italic;
  color: #374151;
}

.course-description :deep(u) {
  text-decoration: underline;
  text-decoration-color: #e63946;
  text-decoration-thickness: 2px;
  text-underline-offset: 3px;
}

.course-description :deep(mark) {
  background: linear-gradient(180deg, transparent 50%, #fef3c7 50%);
  padding: 2px 6px;
  border-radius: 3px;
}

/* Цитаты */
.course-description :deep(blockquote) {
  margin: 32px 0;
  padding: 24px 32px;
  background: linear-gradient(135deg, #f9fafb 0%, #f3f4f6 100%);
  border-left: 6px solid #e63946;
  border-radius: 8px;
  font-style: italic;
  font-size: 19px;
  color: #1f2937;
  position: relative;
}

.course-description :deep(blockquote)::before {
  content: '"';
  position: absolute;
  top: 16px;
  left: 16px;
  font-size: 48px;
  color: #e5e7eb;
  font-family: Georgia, serif;
  line-height: 1;
}

.course-description :deep(blockquote p) {
  margin: 0;
  padding-left: 24px;
}

/* Код */
.course-description :deep(code) {
  background: #f3f4f6;
  color: #e63946;
  padding: 3px 8px;
  border-radius: 4px;
  font-family: 'Courier New', monospace;
  font-size: 16px;
  font-weight: 600;
}

.course-description :deep(pre) {
  background: #1f2937;
  color: #f9fafb;
  padding: 20px 24px;
  border-radius: 10px;
  overflow-x: auto;
  margin: 24px 0;
  box-shadow: 0 4px 12px rgba(0, 0, 0, 0.15);
}

.course-description :deep(pre code) {
  background: transparent;
  color: #10b981;
  padding: 0;
  font-size: 15px;
  font-weight: 400;
}

/* Горизонтальная линия */
.course-description :deep(hr) {
  border: none;
  height: 3px;
  background: linear-gradient(90deg, transparent 0%, #e63946 50%, transparent 100%);
  margin: 40px 0;
}

/* Ссылки */
.course-description :deep(a) {
  color: #e63946;
  text-decoration: none;
  font-weight: 600;
  border-bottom: 2px solid transparent;
  transition: border-color 0.2s ease;
}

.course-description :deep(a:hover) {
  border-bottom-color: #e63946;
}

/* Таблицы */
.course-description :deep(table) {
  width: 100%;
  border-collapse: collapse;
  margin: 32px 0;
  background: white;
  border-radius: 12px;
  overflow: hidden;
  box-shadow: 0 4px 12px rgba(0, 0, 0, 0.08);
}

.course-description :deep(table th) {
  background: linear-gradient(135deg, #e63946 0%, #d62839 100%);
  color: white;
  padding: 16px;
  text-align: left;
  font-weight: 700;
  font-size: 16px;
}

.course-description :deep(table td) {
  padding: 14px 16px;
  border-bottom: 1px solid #e5e7eb;
  color: #374151;
}

.course-description :deep(table tr:last-child td) {
  border-bottom: none;
}

.course-description :deep(table tr:hover td) {
  background: #f9fafb;
}

/* Course Program */
.course-program {
  background: #f9fafb;
  border-radius: 16px;
  padding: 32px;
}

.program-title {
  font-size: 32px;
  font-weight: 700;
  color: #111827;
  margin-bottom: 24px;
}

.modules-list {
  display: flex;
  flex-direction: column;
  gap: 24px;
}

.module-item {
  background: white;
  border-radius: 12px;
  padding: 24px;
  box-shadow: 0 2px 8px rgba(0, 0, 0, 0.06);
}

.module-header {
  display: flex;
  gap: 16px;
  margin-bottom: 16px;
}

.module-number {
  flex-shrink: 0;
  width: 40px;
  height: 40px;
  background: linear-gradient(135deg, #e63946 0%, #d62839 100%);
  color: white;
  border-radius: 50%;
  display: flex;
  align-items: center;
  justify-content: center;
  font-weight: 700;
  font-size: 18px;
}

.module-info {
  flex: 1;
}

.module-info h3 {
  font-size: 20px;
  font-weight: 700;
  color: #111827;
  margin: 0 0 8px;
}

.module-info p {
  color: #6b7280;
  font-size: 15px;
  margin: 0 0 8px;
  line-height: 1.5;
}

.lessons-count {
  display: inline-block;
  padding: 4px 12px;
  background: #f3f4f6;
  border-radius: 999px;
  font-size: 13px;
  color: #6b7280;
  font-weight: 600;
}

.lessons-list {
  list-style: none;
  padding: 0;
  margin: 0;
  display: flex;
  flex-direction: column;
  gap: 12px;
}

.lesson-item {
  display: flex;
  align-items: center;
  gap: 12px;
  padding: 12px 16px;
  background: #f9fafb;
  border-radius: 8px;
  transition: background 0.2s;
}

.lesson-item:hover {
  background: #f3f4f6;
}

.lesson-icon {
  font-size: 20px;
  flex-shrink: 0;
}

.lesson-details {
  flex: 1;
  display: flex;
  justify-content: space-between;
  align-items: center;
  gap: 12px;
}

.lesson-title {
  color: #374151;
  font-size: 15px;
  font-weight: 500;
}

.lesson-duration {
  color: #9ca3af;
  font-size: 13px;
  font-weight: 600;
  flex-shrink: 0;
}

/* Action Card */
.course-action {
  position: sticky;
  top: 24px;
  height: fit-content;
}

.action-card {
  background: white;
  border: 2px solid #e5e7eb;
  border-radius: 16px;
  padding: 32px;
  box-shadow: 0 4px 12px rgba(0, 0, 0, 0.08);
  transition: all 0.3s ease;
}

.action-card.enrolled {
  border-color: #16a34a;
  background: linear-gradient(to bottom, #f0fdf4 0%, white 100%);
}

.action-card.completed {
  border-color: #3b82f6;
  background: linear-gradient(to bottom, #dbeafe 0%, white 100%);
}

.action-card h3 {
  font-size: 24px;
  font-weight: 700;
  color: #111827;
  margin-bottom: 16px;
}

.action-price {
  font-size: 36px;
  font-weight: 700;
  color: #e63946;
  margin-bottom: 16px;
}

.action-progress {
  margin-bottom: 20px;
  display: flex;
  align-items: center;
  gap: 12px;
}

.action-progress .progress-bar {
  flex: 1;
  height: 12px;
  background: #e5e7eb;
  border-radius: 999px;
  overflow: hidden;
}

.action-progress .progress-fill {
  height: 100%;
  background: linear-gradient(90deg, #e63946 0%, #d62839 100%);
  border-radius: 999px;
  transition: width 0.3s ease;
}

.action-progress .progress-fill.completed {
  background: linear-gradient(90deg, #3b82f6 0%, #2563eb 100%);
}

.action-progress .progress-text {
  font-weight: 700;
  color: #6b7280;
  font-size: 16px;
  min-width: 42px;
}

.action-description {
  color: #6b7280;
  margin-bottom: 24px;
  line-height: 1.6;
}

.action-button {
  width: 100%;
  padding: 16px;
  font-size: 18px;
  font-weight: 700;
}

.action-note {
  margin-top: 16px;
  font-size: 14px;
  color: #9ca3af;
  text-align: center;
  line-height: 1.4;
}

/* CTA Section */
.course-cta {
  background: linear-gradient(135deg, #f3f4f6 0%, #e5e7eb 100%);
  padding: 80px 20px;
  text-align: center;
}

.course-cta h2 {
  font-size: 36px;
  font-weight: 700;
  color: #111827;
  margin-bottom: 16px;
}

.course-cta p {
  font-size: 18px;
  color: #4b5563;
  margin-bottom: 32px;
}

.cta-button {
  padding: 14px 32px;
  font-size: 18px;
}

/* States */
.loading-state,
.error-state {
  text-align: center;
  padding: 100px 20px;
  min-height: 60vh;
  display: flex;
  align-items: center;
  justify-content: center;
}

.loading-spinner {
  width: 60px;
  height: 60px;
  border: 5px solid #f3f4f6;
  border-top-color: #e63946;
  border-radius: 50%;
  animation: spin 1s linear infinite;
  margin: 0 auto 20px;
}

@keyframes spin {
  to { transform: rotate(360deg); }
}

.error-state i {
  font-size: 64px;
  color: #ef4444;
  margin-bottom: 24px;
}

.error-state h2 {
  font-size: 32px;
  color: #111827;
  margin-bottom: 16px;
}

.error-state p {
  font-size: 18px;
  color: #6b7280;
  margin-bottom: 32px;
}

/* Animations */
@keyframes fadeInUp {
  from {
    opacity: 0;
    transform: translateY(30px);
  }
  to {
    opacity: 1;
    transform: translateY(0);
  }
}

.fade-in-up {
  animation: fadeInUp 0.8s ease;
}

/* Responsive */
/* Purchase Form Styles */
.purchase-form {
  display: flex;
  flex-direction: column;
  gap: 16px;
}

.form-group {
  display: flex;
  flex-direction: column;
  gap: 8px;
}

.form-group label {
  font-size: 14px;
  font-weight: 600;
  color: #374151;
}

.form-group input {
  padding: 12px 16px;
  border: 1px solid #d1d5db;
  border-radius: 8px;
  font-size: 15px;
  transition: border-color 0.2s;
}

.form-group input:focus {
  outline: none;
  border-color: #e63946;
}

.purchase-form-card .action-button {
  width: 100%;
  margin-top: 8px;
  background: linear-gradient(135deg, #e63946 0%, #d62839 100%);
  border: none;
  color: #ffffff;
  font-weight: 700;
  font-size: 16px;
  box-shadow: 0 10px 25px rgba(230, 57, 70, 0.35);
}

.purchase-form-card .action-button:disabled {
  opacity: 0.7;
  cursor: default;
}

.error-message {
  color: #ef4444;
  font-size: 14px;
  margin-top: 8px;
}

.payment-modal-overlay {
  position: fixed;
  inset: 0;
  background: rgba(15, 23, 42, 0.65);
  display: flex;
  align-items: center;
  justify-content: center;
  z-index: 1000;
}

.payment-modal {
  background: #ffffff;
  padding: 24px;
  border-radius: 16px;
  max-width: 480px;
  width: 100%;
  box-shadow: 0 20px 60px rgba(15, 23, 42, 0.35);
}

#yookassa-widget-container {
  margin-top: 12px;
}

.payment-status-banner {
  margin-bottom: 16px;
  padding: 12px 16px;
  border-radius: 12px;
  background: #ecfdf3;
  border: 1px solid #bbf7d0;
  color: #166534;
  font-size: 14px;
}

.fade-enter-active,
.fade-leave-active {
  transition: opacity 0.2s ease;
}

.fade-enter-from,
.fade-leave-to {
  opacity: 0;
}

@media (max-width: 1024px) {
  .course-description-wrapper {
    grid-template-columns: 1fr;
    gap: 32px;
  }

  .course-action {
    position: static;
  }

  .course-program {
    padding: 24px;
  }

  .program-title {
    font-size: 28px;
  }
}

@media (max-width: 768px) {
  .course-hero-content {
    grid-template-columns: 1fr;
    gap: 32px;
  }

  .course-hero-info h1 {
    font-size: 32px;
  }

  .course-price-badge {
    font-size: 20px;
  }

  .course-hero-image img {
    height: 250px;
  }

  .course-description {
    font-size: 16px;
  }

  .course-description :deep(h1) {
    font-size: 28px;
    margin-top: 32px;
    margin-bottom: 16px;
  }

  .course-description :deep(h2) {
    font-size: 24px;
    margin-top: 28px;
    margin-bottom: 14px;
  }

  .course-description :deep(h3) {
    font-size: 20px;
    margin-top: 24px;
    margin-bottom: 12px;
  }

  .course-description :deep(h4) {
    font-size: 18px;
  }

  .course-description :deep(h5),
  .course-description :deep(h6) {
    font-size: 16px;
  }

  .course-description :deep(p:first-of-type) {
    font-size: 17px;
  }

  .course-description :deep(ol li) {
    padding-left: 40px;
  }

  .course-description :deep(ol li)::before {
    width: 28px;
    height: 28px;
    font-size: 14px;
  }

  .course-description :deep(blockquote) {
    padding: 20px 24px;
    font-size: 17px;
  }

  .course-description :deep(blockquote)::before {
    font-size: 36px;
  }

  .course-description :deep(table) {
    font-size: 14px;
  }

  .course-description :deep(table th),
  .course-description :deep(table td) {
    padding: 10px 12px;
  }

  .action-card {
    padding: 24px;
  }

  .action-card h3 {
    font-size: 20px;
  }

  .action-price {
    font-size: 28px;
  }

  .course-cta h2 {
    font-size: 28px;
  }

  .course-cta p {
    font-size: 16px;
  }
}
</style>

